/**
 * FeatureRoute — the per-route gate.
 *
 * Resolves access for its `sectionId` + `pageId` via `useFeatureAccess`.
 * If the page is visible (released, or previewing) it renders the route's
 * children; otherwise it redirects to `/dashboard`. Sub-resource routes
 * (e.g. `/research/imports/:importId`) pass their parent page's id so
 * they inherit its flag.
 *
 * This is the structural backstop for the "off route → redirect" rule:
 * even if the nav/grid filter ever leaks a link, hitting the URL directly
 * still bounces a normal user back to the dashboard.
 */
import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useFeatureAccess } from "../features/useFeatureAccess";

export interface FeatureRouteProps {
  sectionId: string;
  pageId: string;
  children: ReactNode;
}

export function FeatureRoute({ sectionId, pageId, children }: FeatureRouteProps) {
  const access = useFeatureAccess();
  if (!access.page(sectionId, pageId).visible) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}
